import React, { useState } from "react";
import { Link } from "react-router-dom";
import { MdCheckCircleOutline, MdErrorOutline, MdMailOutline } from "react-icons/md";
import { esqueciSenha } from "../../services/authApi";
import { MENSAGEM_MUITAS_TENTATIVAS } from "./loginError";

const inputCls =
  "w-full rounded-xl border border-gray-200 bg-white px-3 py-3 text-sm text-gray-800 transition-all focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-white/10 dark:bg-navy-900 dark:text-white";

/** Mesma frase que o backend devolve; usada se a resposta vier sem corpo. */
export const MENSAGEM_ESQUECI = "Se o e-mail tiver acesso, enviaremos um link.";
const MENSAGEM_FALHA = "Não foi possível enviar agora. Tente de novo em instantes.";

export function mensagemDeErroDoEsqueci(error: unknown): string {
  const status = (error as { response?: { status?: number } } | null | undefined)?.response?.status;
  return status === 429 ? MENSAGEM_MUITAS_TENTATIVAS : MENSAGEM_FALHA;
}

/**
 * Pede o link de redefinição. A resposta é a mesma exista o e-mail ou não (o backend não revela
 * quem tem acesso), então a tela também não distingue: confirma o envio com a frase fixa.
 */
const EsqueciSenha: React.FC = () => {
  const [email, setEmail] = useState("");
  const [enviado, setEnviado] = useState("");
  const [erro, setErro] = useState("");
  const [loading, setLoading] = useState(false);

  const enviar = async (e: React.FormEvent) => {
    e.preventDefault();
    setErro("");
    setLoading(true);
    try {
      const data = await esqueciSenha(email.trim());
      setEnviado(data?.message || MENSAGEM_ESQUECI);
    } catch (err) {
      setErro(mensagemDeErroDoEsqueci(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-dvh w-full items-center justify-center px-6 py-10">
      <main className="animate-fade-up w-full max-w-sm">
        <h1 className="text-2xl font-extrabold tracking-tight text-navy-800 dark:text-white">
          Esqueci minha senha
        </h1>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
          Informe seu e-mail. Se ele tiver acesso, enviaremos um link para criar uma nova senha.
        </p>

        {enviado ? (
          <div
            role="status"
            className="mt-6 flex items-start gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-sm font-medium text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-900/20 dark:text-emerald-300"
          >
            <MdCheckCircleOutline className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <span>{enviado}</span>
          </div>
        ) : (
          <form onSubmit={enviar} className="mt-6">
            {erro && (
              <div
                role="alert"
                className="mb-4 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-sm font-medium text-red-700 dark:border-red-500/30 dark:bg-red-900/20 dark:text-red-300"
              >
                <MdErrorOutline className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                <span>{erro}</span>
              </div>
            )}
            <div className="flex flex-col gap-1.5">
              <label htmlFor="email" className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                E-mail
              </label>
              <div className="relative">
                <MdMailOutline
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                  aria-hidden="true"
                />
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(ev) => setEmail(ev.target.value)}
                  className={`${inputCls} pl-9`}
                  placeholder="seu@email.com"
                  required
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={loading || email.trim().length < 5}
              className="mt-7 flex w-full items-center justify-center gap-2 rounded-xl bg-navy-800 px-5 py-3 text-sm font-bold text-white shadow-md transition-all hover:bg-navy-900 active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white dark:text-navy-900 dark:hover:bg-gray-100"
            >
              {loading ? "Enviando…" : "Enviar link"}
            </button>
          </form>
        )}

        <Link
          to="/auth/sign-in"
          className="mt-3 block w-full rounded-lg text-center text-sm font-semibold text-gray-600 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:text-gray-300"
        >
          Voltar ao login
        </Link>
      </main>
    </div>
  );
};

export default EsqueciSenha;
